<template>
  <ChatModal name="contacts" title="Contacts" @before-open="reset">
    <div slot="default" :class="$style.container">
      <SearchInput :value="search" :class="$style.searchInput" @input="onInput" @clear="reset" />
      <div v-if="isNotFound" :class="$style.empty">
        <p :class="$style.txt">{{ $t('search.notFound') }}</p>
      </div>
      <template v-else>
        <div :class="$style.section">
          <template v-if="globalSearchUsers.length">
            <div :class="$style.sectionHeader">
              <p :class="$style.sectionTitle">{{ $t('search.globalSearch') }}</p>
              <p
                v-if="loadMoreGlobalSearch && availableUsers.length > 3"
                @click="loadMoreGlobalSearch = false"
                :class="$style.sectionMore"
              >
                {{ $t('search.loadMore') }}
              </p>
            </div>
            <UserList
              :users="globalSearchUsers"
              :class="$style.userList"
              mode="chat"
              :infiniteHandler="findAvailableUsers"
              @go-to-chat="onSelectChat"
            />
          </template>
          <template v-if="users.length">
            <div :class="$style.sectionHeader">
              <p :class="$style.sectionTitle">{{ $t('search.myContacts') }}</p>
              <p
                v-if="loadMoreMyContacts && allUsers.length > 3"
                @click="loadMoreMyContacts = false"
                :class="$style.sectionMore"
              >
                {{ $t('search.loadMore') }}
              </p>
            </div>
            <UserList
              :users="users"
              :class="$style.userList"
              mode="chat"
              :infiniteHandler="infiniteHandler"
              @go-to-chat="onSelectChat"
            />
          </template>
        </div>
      </template>
    </div>
    <!-- <template v-slot:footer>
      <BaseButton oval style="width: auto">Add new contact</BaseButton>
    </template> -->
  </ChatModal>
</template>

<script>
import ChatModal from './ChatModal'
import UserList from '@/components/UserList'
import SearchInput from '@/components/SearchInput'
import { mapActions, mapGetters } from 'vuex'
import { debounce } from 'lodash'
import api from '@/api'

const LIMIT = 40

export default {
  name: 'contacts-popup',
  components: {
    ChatModal,
    UserList,
    SearchInput
  },
  data () {
    return {
      loadMoreGlobalSearch: true,
      loadMoreMyContacts: true,
      search: '',
      availableUsers: []
    }
  },
  computed: {
    ...mapGetters({
      contacts: 'entities/users/contacts',
      searchContacts: 'entities/users/searchContacts'
    }),
    searchQuery () {
      const search = this.search.trim()
      if (search.length < 3) return ''
      return search
    },
    allUsers () {
      return this.searchQuery ? this.searchContacts(this.searchQuery) : this.contacts
    },
    users () {
      return this.loadMoreMyContacts ? this.allUsers.slice(0, 3) : this.allUsers
    },
    globalSearchUsers () {
      return this.loadMoreGlobalSearch ? this.filteredAvailableUsers.slice(0, 3) : this.filteredAvailableUsers
    },
    filteredAvailableUsers () {
      return this.availableUsers?.filter(e => !(e?.nickname?.includes('wa:') || e?.nickname?.includes('tg:'))) || []
    },
    currentPageOfAvailableContacts () {
      return Math.floor(this.availableUsers.length / LIMIT) || 1
    },
    currentPageOfYourContacts () {
      return Math.floor(this.contacts.length / LIMIT) || 1
    },
    isNotFound () {
      return !this.availableUsers.length && !this.users.length
    }
  },
  watch: {
    searchQuery (newValue, oldValue) {
      if (!newValue || newValue !== oldValue) {
        this.availableUsers = []
      }
      this.findAvailableUsers()
      this.loadMoreGlobalSearch = true
      this.loadMoreMyContacts = true
    }
  },
  methods: {
    ...mapActions({
      fetchContactsUsers: 'entities/users/fetchContactsUsers'
    }),
    onInput: debounce(async function (query) {
      this.search = query
    }, 800),
    findAvailableUsers ($state) {
      if (
        !this.searchQuery ||
        (this.availableUsers.length > 0 && this.availableUsers.length < this.currentPageOfAvailableContacts * LIMIT)
      ) {
        return $state?.complete()
      }

      const nextPage =
        this.availableUsers.length === 0 ? this.currentPageOfAvailableContacts : this.currentPageOfAvailableContacts + 1
      const params = { value: this.searchQuery, page: nextPage, limit: LIMIT }
      api.user.publicSearch({ params }).then(({ data }) => {
        this.availableUsers.push(...data)
        if (data.length === LIMIT) {
          $state?.loaded()
        } else {
          $state?.complete()
        }
      })
    },
    reset () {
      this.search = ''
      this.availableUsers = []
    },
    onSelectChat (chat) {
      this.$modal.hide('contacts')
    },
    async infiniteHandler ($state) {
      const nextPage = this.contacts.length === 0 ? this.currentPageOfYourContacts : this.currentPageOfYourContacts + 1
      const data = await this.fetchContactsUsers({
        page: nextPage,
        limit: LIMIT
      })
      if (data.length && data.length === LIMIT) {
        $state.loaded()
      } else {
        $state.complete()
      }
    }
  }
}
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  height: 600px;
}
.section {
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  &Header {
    align-items: flex-end;
    border-bottom: 1px solid $border-color;
    display: flex;
    justify-content: space-between;
    padding: 0 15px;
    height: 40px;
  }
  &Title {
    color: $text-colored;
    font-size: 18px;
    font-weight: 500;
    line-height: 30px;
    margin-top: 10px;
  }
  &More {
    color: $text-dark;
    cursor: pointer;
    font-size: 16px;
    font-weight: 400;
    line-height: 40px;
    &:hover {
      color: $black;
    }
  }
}
.userList {
  & > li {
    border-bottom: 1px solid $border-color;
    padding-left: 15px;
    padding-right: 15px;
  }
}
.empty {
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
}
</style>

<style lang="scss" scoped>
::v-deep {
  .infinite-loading-container {
    display: none;
  }
}
</style>
